import * as React from "react";
import { ExportReportPdfButton } from "@/components/ExportReportPdfButton";
import { useFinance } from "@/context/FinanceContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronRight, Wallet, Copy, RotateCcw } from "lucide-react";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useSupabaseSync } from "@/hooks/useSupabaseSync";
import { useAuth } from "@/context/AuthContext";
import { useBudgets } from "@/hooks/finance/useBudgets";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { currencyFormatter, currencyFormatterCompact } from "@/lib/formatters";

interface Level1Data {
  id: string;
  code: string;
  name: string;
}
interface Level2Data {
  id: string;
  level1Id: string;
  code: string;
  name: string;
  type: "receita" | "despesa";
}
interface Level3Data {
  id: string;
  level2Id?: string;
  code?: string;
  name: string;
  type: "receita" | "despesa";
}

function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export default function Orcamento() {
  const { user } = useAuth();
  const { transactions, costCenters } = useFinance();
  const supabaseSync = useSupabaseSync();

  const now = new Date();
  const [year, setYear] = React.useState<number>(now.getFullYear());
  const [month, setMonth] = React.useState<number>(now.getMonth() + 1); // 1-12
  const [selectedCostCenter, setSelectedCostCenter] = React.useState<string>("all");
  const [expandedNodes, setExpandedNodes] = React.useState<Set<string>>(new Set());

  // Hierarquia
  const [level1Accounts, setLevel1Accounts] = React.useState<Level1Data[]>([]);
  const [level2Accounts, setLevel2Accounts] = React.useState<Level2Data[]>([]);
  const [level3Accounts, setLevel3Accounts] = React.useState<Level3Data[]>([]);

  // Orçamentos do período
  const { budgets, setBudgetValue, bulkUpsert, fetchBudgetsForPeriod } = useBudgets({ year, month });

  React.useEffect(() => {
    document.title = "Orçamento | Radar Financeiro";
  }, []);

  React.useEffect(() => {
    if (!user) return;
    (async () => {
      const [l1, l2, l3] = await Promise.all([
        supabaseSync.fetchChartOfAccountsLevel1(),
        supabaseSync.fetchChartOfAccountsLevel2(),
        supabaseSync.fetchChartOfAccounts(),
      ]);
      setLevel1Accounts(l1.map((d) => ({ id: d.id, code: d.code, name: d.name })));
      setLevel2Accounts(
        l2.map((d) => ({
          id: d.id,
          level1Id: d.level1_id,
          code: d.code,
          name: d.name,
          type: d.type as "receita" | "despesa",
        }))
      );
      setLevel3Accounts(
        l3.filter((d) => !d.hidden).map((d) => ({
          id: d.id,
          level2Id: d.level2_id,
          code: d.code,
          name: d.name,
          type: d.type as "receita" | "despesa",
        }))
      );
    })();
  }, [user]);

  // Período do mês selecionado (usado para Realizado)
  const startDate = React.useMemo(
    () => startOfMonth(new Date(year, month - 1, 1)),
    [year, month]
  );
  const endDate = React.useMemo(
    () => endOfMonth(new Date(year, month - 1, 1)),
    [year, month]
  );

  // Filtra transações do período (mesma regra do DRE: por emissão, sem transferências)
  const filteredTransactions = React.useMemo(() => {
    const start = startDate;
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return transactions.filter((t: any) => {
      if (t.kind === "transferencia") return false;
      if (selectedCostCenter !== "all" && t.costCenterId !== selectedCostCenter) return false;
      if (!t.issueDate) return false;
      const d = parseLocalDate(t.issueDate);
      return d >= start && d <= end;
    });
  }, [transactions, startDate, endDate, selectedCostCenter]);

  // Realizado por conta de Nível 3 (signed: receita +, despesa -)
  const realizadoByL3 = React.useMemo(() => {
    const map: Record<string, number> = {};
    level3Accounts.forEach((l3) => {
      const txs = filteredTransactions.filter((t: any) => t.categoryId === l3.id);
      map[l3.id] = txs.reduce((sum, t: any) => {
        return sum + (t.type === "receita" ? t.value : -t.value);
      }, 0);
    });
    return map;
  }, [filteredTransactions, level3Accounts]);

  // Agrega Realizado para Nível 2 e Nível 1
  const realizadoByL2 = React.useMemo(() => {
    const map: Record<string, number> = {};
    level2Accounts.forEach((l2) => {
      const childL3 = level3Accounts.filter((l3) => l3.level2Id === l2.id);
      map[l2.id] = childL3.reduce((s, l3) => s + (realizadoByL3[l3.id] || 0), 0);
    });
    return map;
  }, [level2Accounts, level3Accounts, realizadoByL3]);

  const realizadoByL1 = React.useMemo(() => {
    const map: Record<string, number> = {};
    level1Accounts.forEach((l1) => {
      const childL2 = level2Accounts.filter((l2) => l2.level1Id === l1.id);
      map[l1.id] = childL2.reduce((s, l2) => s + (realizadoByL2[l2.id] || 0), 0);
    });
    return map;
  }, [level1Accounts, level2Accounts, realizadoByL2]);

  // Orçado agregado (sempre soma das folhas)
  const orcadoByL2 = React.useMemo(() => {
    const map: Record<string, number> = {};
    level2Accounts.forEach((l2) => {
      const childL3 = level3Accounts.filter((l3) => l3.level2Id === l2.id);
      // Aplicamos sinal pelo tipo (receita +, despesa -) p/ resultado coerente
      map[l2.id] = childL3.reduce((s, l3) => {
        const v = budgets[l3.id] || 0;
        return s + (l3.type === "receita" ? v : -v);
      }, 0);
    });
    return map;
  }, [level2Accounts, level3Accounts, budgets]);

  const orcadoByL1 = React.useMemo(() => {
    const map: Record<string, number> = {};
    level1Accounts.forEach((l1) => {
      const childL2 = level2Accounts.filter((l2) => l2.level1Id === l1.id);
      map[l1.id] = childL2.reduce((s, l2) => s + (orcadoByL2[l2.id] || 0), 0);
    });
    return map;
  }, [level1Accounts, level2Accounts, orcadoByL2]);

  // Totais para KPIs (sempre absolutos: receita total e despesa total)
  const totals = React.useMemo(() => {
    const receitaReal = filteredTransactions
      .filter((t: any) => t.type === "receita")
      .reduce((s, t: any) => s + t.value, 0);
    const despesaReal = filteredTransactions
      .filter((t: any) => t.type === "despesa")
      .reduce((s, t: any) => s + t.value, 0);

    let receitaOrc = 0;
    let despesaOrc = 0;
    level3Accounts.forEach((l3) => {
      const v = budgets[l3.id] || 0;
      if (l3.type === "receita") receitaOrc += v;
      else despesaOrc += v;
    });

    const realizadoLiq = receitaReal - despesaReal;
    const orcadoLiq = receitaOrc - despesaOrc;
    const diff = realizadoLiq - orcadoLiq;
    const aderencia = orcadoLiq !== 0 ? (realizadoLiq / orcadoLiq) * 100 : 0;

    return {
      receitaReal,
      despesaReal,
      receitaOrc,
      despesaOrc,
      realizadoLiq,
      orcadoLiq,
      diff,
      aderencia,
    };
  }, [filteredTransactions, level3Accounts, budgets]);

  // Dados do gráfico (por Nível 1, em valores absolutos para visualização)
  const chartData = React.useMemo(() => {
    return level1Accounts
      .slice()
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
      .map((l1) => ({
        name: l1.name.length > 18 ? l1.name.slice(0, 18) + "…" : l1.name,
        Realizado: Math.abs(realizadoByL1[l1.id] || 0),
        Orçado: Math.abs(orcadoByL1[l1.id] || 0),
      }));
  }, [level1Accounts, realizadoByL1, orcadoByL1]);

  const toggleExpand = (id: string) => {
    setExpandedNodes((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const expandAll = () => {
    const ids = new Set<string>();
    level1Accounts.forEach((l1) => ids.add(`l1-${l1.id}`));
    level2Accounts.forEach((l2) => ids.add(`l2-${l2.id}`));
    setExpandedNodes(ids);
  };
  const collapseAll = () => setExpandedNodes(new Set());

  // Cores semânticas (lógica varia por tipo)
  const getColor = (
    realizado: number,
    orcado: number,
    type: "receita" | "despesa" | "resultado"
  ) => {
    if (orcado === 0 && realizado === 0) return "text-muted-foreground";
    if (type === "despesa") {
      // despesa: gastar menos é bom
      const absR = Math.abs(realizado);
      const absO = Math.abs(orcado);
      if (absO === 0) return "text-muted-foreground";
      return absR <= absO ? "text-emerald-600" : "text-red-600";
    }
    // receita ou resultado: realizar mais (ou igual) é bom
    if (orcado === 0) return "text-muted-foreground";
    return realizado >= orcado ? "text-emerald-600" : "text-red-600";
  };

  const formatPct = (realizado: number, orcado: number, type: "receita" | "despesa" | "resultado") => {
    if (orcado === 0) return "—";
    const ref = type === "despesa" ? Math.abs(orcado) : orcado;
    const val = type === "despesa" ? Math.abs(realizado) : realizado;
    const pct = (val / ref) * 100;
    if (!isFinite(pct)) return "—";
    return `${pct.toFixed(0)}%`;
  };

  // Ações em massa
  const handleCopyRealizado = async () => {
    const entries = level3Accounts.map((l3) => {
      const realizado = realizadoByL3[l3.id] || 0;
      const amount = Math.abs(realizado);
      return { chartOfAccountsId: l3.id, amount };
    });
    await bulkUpsert(entries);
  };

  const handleRepeatPrevMonth = async () => {
    const prevDate = new Date(year, month - 2, 1); // mes anterior
    const prevYear = prevDate.getFullYear();
    const prevMonth = prevDate.getMonth() + 1;
    const prev = await fetchBudgetsForPeriod(prevYear, prevMonth);
    const entries = Object.entries(prev).map(([id, amount]) => ({
      chartOfAccountsId: id,
      amount,
    }));
    if (entries.length === 0) return;
    await bulkUpsert(entries);
  };

  // Cell editável (apenas Nível 3)
  const BudgetCell: React.FC<{ accountId: string }> = ({ accountId }) => {
    const value = budgets[accountId] || 0;
    const [local, setLocal] = React.useState<string>(value === 0 ? "" : String(value));

    React.useEffect(() => {
      setLocal(value === 0 ? "" : String(value));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const commit = () => {
      const num = Number(local.replace(",", ".")) || 0;
      const safe = num < 0 ? 0 : num;
      if (safe !== value) setBudgetValue(accountId, safe);
    };

    return (
      <Input
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        value={local}
        placeholder="0,00"
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            (e.currentTarget as HTMLInputElement).blur();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className="h-8 text-center text-sm"
      />
    );
  };

  // Render Nível 3
  const renderLevel3 = (l3: Level3Data) => {
    const real = realizadoByL3[l3.id] || 0;
    const orc = budgets[l3.id] || 0;
    const orcSigned = l3.type === "receita" ? orc : -orc;
    const diff = real - orcSigned;
    const colorReal = getColor(real, orcSigned, l3.type);
    const colorDiff = getColor(real, orcSigned, l3.type);

    return (
      <TableRow key={`l3-${l3.id}`} className="hover:bg-muted/30 transition-colors">
        <TableCell className="pl-16 text-[13px]">
          {l3.code} - {l3.name}
        </TableCell>
        <TableCell className={`text-center text-sm ${colorReal}`}>
          {real !== 0 ? currencyFormatter.format(real) : "—"}
        </TableCell>
        <TableCell className="text-center w-[160px]">
          <BudgetCell accountId={l3.id} />
        </TableCell>
        <TableCell className={`text-center text-sm ${colorDiff}`}>
          {orc !== 0 || real !== 0 ? currencyFormatter.format(diff) : "—"}
        </TableCell>
        <TableCell className={`text-center text-sm ${colorDiff}`}>
          {formatPct(real, orcSigned, l3.type)}
        </TableCell>
      </TableRow>
    );
  };

  // Render Nível 2
  const renderLevel2 = (l2: Level2Data) => {
    const nodeId = `l2-${l2.id}`;
    const expanded = expandedNodes.has(nodeId);
    const real = realizadoByL2[l2.id] || 0;
    const orc = orcadoByL2[l2.id] || 0;
    const diff = real - orc;
    const childL3 = level3Accounts
      .filter((l3) => l3.level2Id === l2.id)
      .sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));
    const color = getColor(real, orc, l2.type);

    const rows: React.ReactNode[] = [];
    rows.push(
      <TableRow
        key={nodeId}
        className="cursor-pointer hover:opacity-90 transition-colors"
        style={{ backgroundColor: "#FFFBEB" }}
        onClick={() => toggleExpand(nodeId)}
      >
        <TableCell className="pl-8 font-semibold text-sm">
          <div className="flex items-center gap-2">
            {childL3.length > 0 ? (
              expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            ) : (
              <span className="w-4" />
            )}
            <span>{l2.code} - {l2.name}</span>
          </div>
        </TableCell>
        <TableCell className={`text-center text-sm font-semibold ${color}`}>
          {real !== 0 ? currencyFormatter.format(real) : "—"}
        </TableCell>
        <TableCell className="text-center text-sm font-semibold text-muted-foreground">
          {orc !== 0 ? currencyFormatter.format(orc) : "—"}
        </TableCell>
        <TableCell className={`text-center text-sm font-semibold ${color}`}>
          {real !== 0 || orc !== 0 ? currencyFormatter.format(diff) : "—"}
        </TableCell>
        <TableCell className={`text-center text-sm font-semibold ${color}`}>
          {formatPct(real, orc, l2.type)}
        </TableCell>
      </TableRow>
    );
    if (expanded) childL3.forEach((l3) => rows.push(renderLevel3(l3)));
    return rows;
  };

  // Render Nível 1
  const renderLevel1 = (l1: Level1Data) => {
    const nodeId = `l1-${l1.id}`;
    const expanded = expandedNodes.has(nodeId);
    const real = realizadoByL1[l1.id] || 0;
    const orc = orcadoByL1[l1.id] || 0;
    const diff = real - orc;
    const childL2 = level2Accounts
      .filter((l2) => l2.level1Id === l1.id)
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

    // Tipo predominante para colorir Nível 1
    const hasReceita = childL2.some((l2) => l2.type === "receita");
    const hasDespesa = childL2.some((l2) => l2.type === "despesa");
    const type: "receita" | "despesa" | "resultado" =
      hasReceita && !hasDespesa ? "receita" : !hasReceita && hasDespesa ? "despesa" : "resultado";
    const color = getColor(real, orc, type);

    const rows: React.ReactNode[] = [];
    rows.push(
      <TableRow
        key={nodeId}
        className="cursor-pointer hover:opacity-90 border-t-2 border-border transition-colors"
        style={{ backgroundColor: "#FFEDD5" }}
        onClick={() => toggleExpand(nodeId)}
      >
        <TableCell className="pl-3 font-bold text-[15px]">
          <div className="flex items-center gap-2">
            {childL2.length > 0 ? (
              expanded ? <ChevronDown className="h-5 w-5 text-primary" /> : <ChevronRight className="h-5 w-5 text-primary" />
            ) : (
              <span className="w-5" />
            )}
            <span>{l1.code} - {l1.name}</span>
          </div>
        </TableCell>
        <TableCell className={`text-center font-bold text-[15px] ${color}`}>
          {real !== 0 ? currencyFormatter.format(real) : "—"}
        </TableCell>
        <TableCell className="text-center font-bold text-[15px] text-muted-foreground">
          {orc !== 0 ? currencyFormatter.format(orc) : "—"}
        </TableCell>
        <TableCell className={`text-center font-bold text-[15px] ${color}`}>
          {real !== 0 || orc !== 0 ? currencyFormatter.format(diff) : "—"}
        </TableCell>
        <TableCell className={`text-center font-bold text-[15px] ${color}`}>
          {formatPct(real, orc, type)}
        </TableCell>
      </TableRow>
    );
    if (expanded) childL2.forEach((l2) => rows.push(...renderLevel2(l2)));
    return rows;
  };

  const sortedL1 = [...level1Accounts].sort((a, b) =>
    a.code.localeCompare(b.code, undefined, { numeric: true })
  );

  const colorResultado = getColor(totals.realizadoLiq, totals.orcadoLiq, "resultado");

  // Anos disponíveis no seletor (atual ± 3)
  const yearOptions = React.useMemo(() => {
    const cur = now.getFullYear();
    return [cur - 2, cur - 1, cur, cur + 1, cur + 2];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main ref={reportRef} className="page-bleed py-6 space-y-6">
        {/* Title */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Wallet className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Orçamento</h1>
              <p className="text-sm text-muted-foreground">
                Compare o realizado dos lançamentos com o orçado por conta. Edite os valores diretamente na tabela.
              </p>
            </div>
          </div>
          <ExportReportPdfButton
            targetRef={reportRef}
            title="Orçamento"
            subtitle={`Competência: ${String(month).padStart(2, "0")}/${year}`}
            filenameSlug="orcamento"
          />
        </div>


        {/* Filtros */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-4">
              <div className="space-y-2 w-full sm:w-auto">
                <Label>Mês</Label>
                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                  <SelectTrigger className="w-full sm:w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 w-full sm:w-auto">
                <Label>Ano</Label>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                  <SelectTrigger className="w-full sm:w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 w-full sm:w-auto">
                <Label>Centro de Custo</Label>
                <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
                  <SelectTrigger className="w-full sm:w-[200px]">
                    <SelectValue placeholder="Todos os centros" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os centros</SelectItem>
                    {costCenters.map((cc) => (
                      <SelectItem key={cc.id} value={cc.id}>{cc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 ml-auto flex-wrap">
                <Button variant="outline" size="sm" onClick={handleCopyRealizado} title="Copia o valor Realizado para o Orçado de cada conta">
                  <Copy className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">Copiar Realizado → Orçado</span>
                </Button>
                <Button variant="outline" size="sm" onClick={handleRepeatPrevMonth} title="Copia os orçamentos do mês anterior">
                  <RotateCcw className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">Repetir mês anterior</span>
                </Button>
                <Button variant="outline" size="sm" onClick={expandAll}>Expandir Tudo</Button>
                <Button variant="outline" size="sm" onClick={collapseAll}>Recolher Tudo</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="py-4">
              <div className="text-sm text-muted-foreground font-medium">Realizado (Resultado)</div>
              <div className="text-2xl font-bold">{currencyFormatter.format(totals.realizadoLiq)}</div>
              <div className="text-xs text-muted-foreground mt-1">
                Receita {currencyFormatterCompact.format(totals.receitaReal)} · Despesa {currencyFormatterCompact.format(totals.despesaReal)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="text-sm text-muted-foreground font-medium">Orçado (Resultado)</div>
              <div className="text-2xl font-bold">{currencyFormatter.format(totals.orcadoLiq)}</div>
              <div className="text-xs text-muted-foreground mt-1">
                Receita {currencyFormatterCompact.format(totals.receitaOrc)} · Despesa {currencyFormatterCompact.format(totals.despesaOrc)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="text-sm text-muted-foreground font-medium">Diferença</div>
              <div className={`text-2xl font-bold ${colorResultado}`}>
                {currencyFormatter.format(totals.diff)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="text-sm text-muted-foreground font-medium">Aderência</div>
              <div className={`text-2xl font-bold ${colorResultado}`}>
                {totals.orcadoLiq !== 0 ? `${totals.aderencia.toFixed(0)}%` : "—"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {format(startDate, "MMMM 'de' yyyy", { locale: ptBR })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gráfico */}
        {chartData.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Realizado vs Orçado por Grupo</CardTitle>
            </CardHeader>
            <CardContent className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis
                    tickFormatter={(v) => currencyFormatterCompact.format(v as number)}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip formatter={(v: any) => currencyFormatter.format(Number(v))} />
                  <Legend />
                  <Bar dataKey="Orçado" fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Realizado" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Tabela */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-lg">Orçamento por Conta</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[640px]">
              <Table>
                <TableHeader className="sticky top-0 z-20 bg-muted">
                  <TableRow>
                    <TableHead className="min-w-[280px]">Conta</TableHead>
                    <TableHead className="text-center min-w-[140px]">Realizado</TableHead>
                    <TableHead className="text-center min-w-[160px]">Orçado</TableHead>
                    <TableHead className="text-center min-w-[140px]">Diferença</TableHead>
                    <TableHead className="text-center min-w-[80px]">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedL1.length > 0 ? (
                    sortedL1.flatMap((l1) => renderLevel1(l1))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Nenhuma conta cadastrada. Configure o Plano de Contas em Cadastros.
                      </TableCell>
                    </TableRow>
                  )}
                  {/* Resultado total */}
                  {sortedL1.length > 0 && (
                    <TableRow
                      className="border-t-2 border-primary font-bold sticky bottom-0"
                      style={{ backgroundColor: "#FFEDD5" }}
                    >
                      <TableCell className="font-bold text-lg pl-3">RESULTADO</TableCell>
                      <TableCell className={`text-center font-bold text-lg ${colorResultado}`}>
                        {currencyFormatter.format(totals.realizadoLiq)}
                      </TableCell>
                      <TableCell className="text-center font-bold text-lg text-muted-foreground">
                        {currencyFormatter.format(totals.orcadoLiq)}
                      </TableCell>
                      <TableCell className={`text-center font-bold text-lg ${colorResultado}`}>
                        {currencyFormatter.format(totals.diff)}
                      </TableCell>
                      <TableCell className={`text-center font-bold text-lg ${colorResultado}`}>
                        {totals.orcadoLiq !== 0 ? `${totals.aderencia.toFixed(0)}%` : "—"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
